import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { Button } from "@/components/ui/button"
import { ArrowRight, Calendar, Clock, ChevronRight } from "lucide-react"
import { Link } from "react-router-dom"
import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { blogsInsightsFAQs } from "@/data/faqData"

const blogPosts = [
  {
    category: "AIOps Strategy",
    title: "Why Enterprise AIOps Strategy Needs Managed Services in 2026",
    excerpt:
      "Self-learning AI platforms can cut IT operational costs by 50% through AIOps strategy implementation. Learn how managed services create the foundation for success.",
    date: "Feb 1, 2026",
    readTime: "12 min read",
    author: "RSA Tech Group Research Team",
    image:
      "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&q=80&w=800",
    slug: "/blogs/aiops-strategy-2026",
  },
]

const BlogsInsights = () => {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      <SEO
        title="Blogs & Insights | RSA Tech Group"
        description="Deep dives into the algorithms, architectures, and strategies defining the next era of intelligence."
        image="/blogs-hero-bg.png"
      />
      <Navigation />

      {/* ───── S1 Hero - Dark ───── */}
      <section className="relative pt-40 pb-24 overflow-hidden min-h-[80vh] flex items-center justify-center bg-[#0f1629]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(28,43,110,0.5),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(28,43,110,0.3),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_40%,rgba(253,164,0,0.08),transparent_70%)]" />

        {/* Glow orbs */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#FDA400]/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#1C2B6E]/[0.15] rounded-full blur-[120px]" />

        <div className="section-container relative z-10 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px w-10 bg-[#FDA400]" />
            <span className="text-[#FDA400] text-xs font-semibold uppercase tracking-[0.2em]">
              Blogs & Insights
            </span>
            <div className="h-px w-10 bg-[#FDA400]" />
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6 max-w-4xl mx-auto">
            Algorithms, Architectures &{" "}
            <span className="text-[#FDA400]">Strategies</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-400 leading-relaxed font-light max-w-2xl mx-auto">
            Deep dives into the algorithms, architectures, and strategies
            defining the next era of intelligence.
          </p>
        </div>
      </section>

      {/* ───── S2 Featured Article - Light + Dot Grid ───── */}
      <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
        {/* Dot grid bg */}
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage:
              "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
            backgroundSize: "28px 28px",
          }}
        />
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#FDA400]" />
              <span className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em]">
                Featured
              </span>
              <div className="h-px w-10 bg-[#FDA400]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
              Editor's <span className="text-[#FDA400]">Pick</span>
            </h2>
          </div>

          <Link to="/blogs/healthcare-it-trends-2026" className="partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2 block">
            {/* Top accent */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />

            {/* Glow */}
            <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

            <div className="grid lg:grid-cols-2 gap-0 relative z-10">
              <div className="relative overflow-hidden h-72 lg:h-auto">
                <img
                  src="/images/blog-2/1.WEBP"
                  alt="Healthcare IT Trends 2026"
                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10" />
              </div>
              <div className="p-8 md:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-4 text-sm mb-6">
                  <span className="bg-[#1C2B6E]/[0.08] text-[#1C2B6E] px-3 py-1 rounded-full font-semibold text-xs uppercase tracking-wider">
                    Healthcare IT
                  </span>
                  <span className="flex items-center gap-1 text-slate-400 text-xs">
                    <Clock className="w-3.5 h-3.5" /> 15 min read
                  </span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold text-[#1C2B6E] leading-tight mb-4">
                  Healthcare IT Trends 2026: Critical Insights Before{" "}
                  <span className="text-[#FDA400]">HIMSS</span>
                </h3>
                <p className="text-slate-500 leading-relaxed mb-8">
                  AI commands 54% of healthcare VC investments while physician
                  adoption surges from 38% to 66%. Explore the trends shaping
                  clinical AI, RCM automation, and cloud interoperability.
                </p>
                <div>
                  <Button
                    size="lg"
                    className="bg-[#1C2B6E] hover:bg-[#162258] text-white font-bold text-sm px-8 py-6 h-auto rounded-lg shadow-lg shadow-[#1C2B6E]/20 transition-all"
                  >
                    Read Full Analysis{" "}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* ───── S3 Blog Grid - Dark ───── */}
      <section className="relative py-24 overflow-hidden bg-[#0f1629]">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(28,43,110,0.5),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_100%,rgba(28,43,110,0.3),transparent_70%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_40%_at_50%_40%,rgba(253,164,0,0.08),transparent_70%)]" />

        {/* Glow orbs */}
        <div className="absolute top-1/3 left-1/4 w-[600px] h-[600px] bg-[#FDA400]/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/3 w-[400px] h-[400px] bg-[#1C2B6E]/[0.15] rounded-full blur-[120px]" />

        <div className="section-container relative z-10">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="h-px w-10 bg-[#FDA400]" />
              <span className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em]">
                Latest Articles
              </span>
              <div className="h-px w-10 bg-[#FDA400]" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white tracking-tight leading-[1.1]">
              From the <span className="text-[#FDA400]">Lab</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {blogPosts.map((post, idx) => {
              const CardContent = (
                <>
                  <div className="h-56 overflow-hidden relative rounded-t-2xl">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0f1629]/60 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-white/[0.08] backdrop-blur-md text-xs font-bold uppercase tracking-wider rounded-full text-[#FDA400] border border-white/[0.08]">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-8 flex flex-col flex-grow">
                    <div className="flex items-center gap-4 text-slate-500 text-xs font-medium mb-4">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {post.date}
                      </span>
                      <span className="text-white/20">&bull;</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[#FDA400] transition-colors leading-tight">
                      {post.title}
                    </h3>
                    <p className="text-slate-400 mb-6 line-clamp-3 leading-relaxed flex-grow">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center text-[#FDA400] font-semibold text-sm group-hover:underline underline-offset-4">
                      Read Article{" "}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </>
              )

              return post.slug ? (
                <Link
                  key={idx}
                  to={post.slug}
                  className="group cursor-pointer flex flex-col bg-white/[0.04] border border-white/[0.08] rounded-2xl overflow-hidden hover:bg-white/[0.06] hover:border-white/[0.12] hover:shadow-2xl hover:shadow-[#FDA400]/[0.05] transition-all duration-300 hover:-translate-y-1"
                >
                  {CardContent}
                </Link>
              ) : (
                <div
                  key={idx}
                  className="group cursor-pointer flex flex-col bg-white/[0.04] border border-white/[0.08] rounded-2xl overflow-hidden hover:bg-white/[0.06] hover:border-white/[0.12] hover:shadow-2xl hover:shadow-[#FDA400]/[0.05] transition-all duration-300 hover:-translate-y-1"
                >
                  {CardContent}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ───── S4 FAQ - Light ───── */}
      <PageFAQ
        faqs={blogsInsightsFAQs}
        title="Blogs FAQs"
        subtitle="Common questions about our blog and content"
        variant="muted"
      />

      <Footer />
    </div>
  )
}

export default BlogsInsights
